
import React, { useState } from 'react';
import { UserProfile } from '../../../app/domain/types/index.ts';
import {
  Users, DollarSign, AlertTriangle, Activity, Search, MoreVertical,
  ShieldCheck, Ban, FileText, CheckCircle, XCircle, BarChart2
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'moderation'>('overview');

  // Mock Data
  const revenueData = [
    { name: 'Week 1', value: 4000 },
    { name: 'Week 2', value: 3000 },
    { name: 'Week 3', value: 5000 },
    { name: 'Week 4', value: 4800 },
    { name: 'Week 5', value: 6000 },
  ];

  const userGrowthData = [
    { name: 'Mon', users: 120 },
    { name: 'Tue', users: 132 },
    { name: 'Wed', users: 101 },
    { name: 'Thu', users: 134 },
    { name: 'Fri', users: 190 },
    { name: 'Sat', users: 230 },
    { name: 'Sun', users: 210 },
  ];

  const flaggedContent = [
    { id: 1, type: 'Post', user: 'JohnDoe123', reason: 'Inappropriate language', date: '2 hours ago', content: 'This diet is stupid and...' },
    { id: 2, type: 'Comment', user: 'SarahSmith', reason: 'Spam/Promotion', date: '5 hours ago', content: 'Buy cheap pills at...' },
    { id: 3, type: 'Profile', user: 'FastWeightLoss', reason: 'Impersonation', date: '1 day ago', content: 'Dr. Oz Official' },
  ];

  const recentUsers = [
    { id: 1, name: 'Alice Cooper', email: 'alice@example.com', role: 'User', status: 'Active', joined: '2 mins ago' },
    { id: 2, name: 'Dr. Robert Lang', email: 'robert.l@clinic.com', role: 'Professional', status: 'Pending', joined: '1 hour ago' },
    { id: 3, name: 'Charlie Brown', email: 'charlie@example.com', role: 'User', status: 'Active', joined: '3 hours ago' },
    { id: 4, name: 'Emily White', email: 'emily@example.com', role: 'User', status: 'Suspended', joined: '1 day ago' },
  ];

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Admin Header */}
      <div className="p-6 md:p-8 pb-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md sticky top-0 z-20">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="text-red-600" /> Admin Dashboard
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Platform Overview & Management</p>
          </div>
          <div className="flex gap-2">
            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-bold flex items-center gap-1">
              <Activity size={12} /> Systems Normal
            </span>
          </div>
        </div>

        <div className="flex gap-6 border-b border-stone-200 dark:border-stone-800">
          {['overview', 'users', 'moderation', 'financials'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`pb-3 text-sm font-medium capitalize transition-colors relative ${
                activeTab === tab ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-red-600 rounded-t-full"></div>}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 md:p-8 no-scrollbar">

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-8 animate-fade-in-up">
            {/* Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-5 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400"><Users size={20} /></div>
                  <span className="text-xs font-bold text-green-500">+12%</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">10,234</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Users</p>
              </div>
              <div className="p-5 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-green-600 dark:text-green-400"><DollarSign size={20} /></div>
                  <span className="text-xs font-bold text-green-500">+8%</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">$42,500</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Monthly Revenue</p>
              </div>
              <div className="p-5 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400"><AlertTriangle size={20} /></div>
                  <span className="text-xs font-bold text-red-500">3 New</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">15</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Flagged Items</p>
              </div>
              <div className="p-5 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-purple-600 dark:text-purple-400"><Activity size={20} /></div>
                  <span className="text-xs font-bold text-gray-400">99.9%</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Healthy</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">System Status</p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Revenue Trend</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#16a34a" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" opacity={0.5} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                      <YAxis axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#16a34a" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">New User Signups</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={userGrowthData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" opacity={0.5} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                      <YAxis axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                      <Tooltip cursor={{fill: 'transparent'}} />
                      <Bar dataKey="users" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={30} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div className="animate-fade-in-up">
            <div className="flex justify-between items-center mb-6">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all"
                />
              </div>
              <button className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-bold">Export CSV</button>
            </div>

            <div className="bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 overflow-hidden">
              <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                <thead className="bg-stone-50 dark:bg-stone-900/50 text-xs uppercase text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="px-6 py-4 font-semibold">User</th>
                    <th className="px-6 py-4 font-semibold">Role</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Joined</th>
                    <th className="px-6 py-4 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 dark:divide-stone-700">
                  {recentUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-stone-600 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300">
                            {user.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                            <div className="text-xs">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{user.role}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          user.status === 'Active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          user.status === 'Pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">{user.joined}</td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-gray-400 hover:text-gray-600 dark:hover:text-white"><MoreVertical size={18} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MODERATION TAB */}
        {activeTab === 'moderation' && (
          <div className="animate-fade-in-up">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Flagged Content Queue</h2>
            <div className="space-y-4">
              {flaggedContent.map((item) => (
                <div key={item.id} className="p-6 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm flex flex-col md:flex-row gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs font-bold uppercase">{item.type}</span>
                      <span className="text-xs text-gray-400">• Reported {item.date}</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      Reason: <span className="font-semibold text-gray-700 dark:text-gray-200">{item.reason}</span>
                    </p>
                    <div className="p-4 bg-stone-50 dark:bg-stone-900 rounded-lg border border-stone-100 dark:border-stone-700">
                      <p className="text-sm text-gray-800 dark:text-gray-200 italic">"{item.content}"</p>
                      <p className="text-xs text-gray-400 mt-2">- Posted by {item.user}</p>
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-2 justify-center min-w-[140px]">
                    <button className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition-colors">
                      <CheckCircle size={16} /> Keep
                    </button>
                    <button className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors">
                      <Ban size={16} /> Ban User
                    </button>
                    <button className="flex items-center justify-center gap-2 px-4 py-2 bg-stone-100 dark:bg-stone-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-bold hover:bg-stone-200 dark:hover:bg-stone-600 transition-colors">
                      <XCircle size={16} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
